import { useAnalyticsConsent } from '@app/hooks';
import { FeedbackType } from '@app/routes/content/routes/recordings/routes/lab/components/feedback';
import { useAppDispatch, useAppSelector } from '@app/store';
import { useCallback, useMemo } from 'react';

import { useCreatorLabAnalytics } from '../../../routes/lab/hooks';
import { ContentType } from '../domain';
import {
  ContentComposerEvent,
  FeedbackType as ContentComposerFeedbackType,
} from '../event';
import { selectContentComposerFeedback } from '../selector';

/**
 * Hook for managing feedback in Content Composer
 *
 * Unlike Creator Lab, Content Composer doesn't have a "selected content" concept.
 * Each content option (OPTION 1, OPTION 2) has its own independent feedback state.
 * Feedback persists per contentId until page refresh.
 *
 * @param contentType - The content type (SOCIAL_POST, BLOG_POST, EMAIL)
 * @param contentId - The content ID to track feedback for (null if content not yet generated)
 * @param optionIndex - The option index (0 for OPTION 1, 1 for OPTION 2)
 * @returns Feedback state and handlers
 */
export const useContentComposerFeedback = (
  contentType: ContentType,
  contentId: string | null,
  optionIndex: number,
) => {
  const dispatch = useAppDispatch();
  const hasAnalyticsConsent = useAnalyticsConsent();

  const {
    trackThumbsUpClicked,
    trackThumbsDownClicked,
    trackFeedbackSubmitted,
  } = useCreatorLabAnalytics();

  const optionType = useMemo(() => {
    if (contentType !== 'SOCIAL_POST') return undefined;
    return optionIndex === 0 ? 'option_1' : 'option_2';
  }, [contentType, optionIndex]);

  const selectedFeedback = useAppSelector(state =>
    contentId
      ? selectContentComposerFeedback(
          state,
          contentType,
          contentId,
          optionIndex,
        )
      : null,
  );

  const shouldShowFeedback = hasAnalyticsConsent && contentId !== null;

  const handleFeedbackChange = useCallback(
    (feedbackType: FeedbackType | null) => {
      if (!contentId) {
        return;
      }

      dispatch({
        type: '[content-composer] feedback changed',
        payload: {
          contentType,
          contentId,
          optionIndex,
          feedbackType: feedbackType as ContentComposerFeedbackType,
        },
      } as ContentComposerEvent);
    },
    [contentId, contentType, optionIndex, dispatch],
  );

  const handleFeedbackSubmit = useCallback(
    (feedbackType: FeedbackType, submittedAdditionalFeedback?: string) => {
      if (!contentId) {
        return;
      }

      dispatch({
        type: '[content-composer] feedback changed',
        payload: {
          contentType,
          contentId,
          optionIndex,
          feedbackType: feedbackType as ContentComposerFeedbackType,
        },
      } as ContentComposerEvent);

      if (feedbackType === 'thumbs_up') {
        trackThumbsUpClicked(optionType);
      } else if (feedbackType === 'thumbs_down') {
        trackFeedbackSubmitted(submittedAdditionalFeedback, optionType);
      }
    },
    [
      contentId,
      contentType,
      optionIndex,
      dispatch,
      optionType,
      trackThumbsUpClicked,
      trackFeedbackSubmitted,
    ],
  );

  const handleThumbsDownClick = useCallback(() => {
    // Track thumbs down click immediately to Mixpanel
    trackThumbsDownClicked(optionType);
  }, [trackThumbsDownClicked, optionType]);

  return {
    selectedFeedback,
    shouldShowFeedback,
    handleFeedbackSubmit,
    handleFeedbackChange,
    handleThumbsDownClick,
    contentId,
  };
};
